import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { audiencesService } from "../../services/audiences";
import {
  HeroStat,
  ScoreRing,
  FitDistributionBars,
  CreatorSizeBreakdown,
  TopCreatorCard,
  TopVideosCard,
  EngagementMetrics,
  VideoPerformanceGrid,
  formatNumber,
} from "../../components/analytics";
import OrbitalLoader from "../../components/common/OrbitalLoader";
import { parsePaginatedResponse } from "../../utils/pagination";

const Analytics = ({ audience }) => {
  const navigate = useNavigate();
  const [creators, setCreators] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!audience?.hash) return;

    const params = { audience_hash: audience.hash, page_size: 1000 };
    let loadedCount = 0;
    const checkLoaded = () => {
      loadedCount++;
      if (loadedCount >= 2) setLoading(false);
    };

    audiencesService.getAudienceCreators(
      params,
      (response) => {
        const { results } = parsePaginatedResponse(response);
        setCreators(results);
        checkLoaded();
      },
      () => checkLoaded()
    );

    audiencesService.getAudienceVideos(
      params,
      (response) => {
        const { results } = parsePaginatedResponse(response);
        setVideos(results);
        checkLoaded();
      },
      () => checkLoaded()
    );
  }, [audience?.hash]);

  // Calculate metrics
  const totalReach = creators.reduce((sum, c) => sum + (c.creator.subscribers || c.creator.follower_count || 0), 0);
  const totalHighFitVideos = videos.filter((v) => v.fit_score != null).length;
  const audienceCreatorFit = (c) => c.category_fit?.fit_score ?? null;
  const avgFitScore = creators.length > 0
    ? creators.reduce((sum, c) => sum + (audienceCreatorFit(c) || 0), 0) / creators.length
    : 0;

  const totalVideoViews = videos.reduce((sum, v) => sum + (v.video?.count_views || 0), 0);
  const totalLikes = videos.reduce((sum, v) => sum + (v.video?.count_likes || 0), 0);
  const totalComments = videos.reduce((sum, v) => sum + (v.video?.count_comments || 0), 0);
  const avgEngagement = totalVideoViews > 0 ? ((totalLikes + totalComments) / totalVideoViews * 100) : 0;

  // Fit distribution
  const fitDistribution = { excellent: 0, good: 0, moderate: 0, poor: 0 };
  creators.forEach((c) => {
    const score = audienceCreatorFit(c) || 0;
    if (score >= 76) fitDistribution.excellent++;
    else if (score >= 51) fitDistribution.good++;
    else if (score >= 26) fitDistribution.moderate++;
    else fitDistribution.poor++;
  });

  // Top creators
  const topCreators = [...creators]
    .sort(
      (a, b) =>
        (b.best_score || audienceCreatorFit(b) || 0) - (a.best_score || audienceCreatorFit(a) || 0)
    )
    .slice(0, 5);

  // Audience Health Score (composite)
  const highFitPct = creators.length > 0 ? (fitDistribution.excellent + fitDistribution.good) / creators.length * 100 : 0;
  const audienceHealthScore = Math.round((avgFitScore * 0.4) + (Math.min(highFitPct, 100) * 0.3) + (Math.min(avgEngagement * 10, 30)));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <OrbitalLoader size={128} />
      </div>
    );
  }

  if (creators.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-theme-secondary flex items-center justify-center">
          <svg className="w-10 h-10 text-theme-text opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        </div>
        <h3 className="text-xl font-semibold text-theme-text mb-2">No Analytics Data Yet</h3>
        <p className="text-theme-text opacity-60 max-w-md mx-auto">
          Analytics will appear once creators and videos are populated for this audience.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Hero Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <HeroStat
          label="Total Reach"
          value={formatNumber(totalReach)}
          subLabel="Combined followers"
          icon={
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          }
        />
        <HeroStat
          label="High-Fit Videos"
          value={totalHighFitVideos}
          subLabel={`From ${creators.length} creators`}
          icon={
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          }
        />
        <HeroStat
          label="Video Views"
          value={formatNumber(totalVideoViews)}
          subLabel={`${avgEngagement.toFixed(2)}% engagement`}
          icon={
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          }
        />
        <HeroStat
          label="Audience Health"
          value={`${audienceHealthScore}/100`}
          subLabel="Quality score"
          icon={
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
      </div>

      {/* Second Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-theme-secondary rounded-xl p-6 border border-theme-secondary">
          <h3 className="text-lg font-semibold text-theme-text mb-6">Fit Score Overview</h3>
          <div className="flex items-center justify-center mb-6">
            <ScoreRing score={avgFitScore} size={140} label="Average" />
          </div>
          <FitDistributionBars distribution={fitDistribution} />
        </div>

        <div className="bg-theme-secondary rounded-xl p-6 border border-theme-secondary">
          <h3 className="text-lg font-semibold text-theme-text mb-6">Creator Tiers</h3>
          <CreatorSizeBreakdown creators={creators} />
          <div className="mt-6 pt-4 border-t border-theme-main">
            <div className="flex justify-between text-sm">
              <span className="text-theme-text opacity-60">Total Creators</span>
              <span className="font-semibold text-theme-text">{creators.length}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Top Performers + Top Videos side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-theme-secondary rounded-xl p-6 border border-theme-secondary">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-theme-text">Top Performers</h3>
            <button
              onClick={() => navigate(`/audience/${audience.hash}/creators/`)}
              className="text-sm text-theme-primary hover:underline"
            >
              View all →
            </button>
          </div>
          <div className="space-y-3">
            {topCreators.map((item, idx) => (
              <TopCreatorCard
                key={item.hash}
                item={item}
                rank={idx + 1}
                onClick={() => navigate(`/audience/${audience.hash}/creators/${item.hash}/`)}
              />
            ))}
          </div>
        </div>

        <div className="bg-theme-secondary rounded-xl p-6 border border-theme-secondary">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-theme-text">Top Videos</h3>
            {videos.length > 0 && (
              <button
                onClick={() => navigate(`/audience/${audience.hash}/videos/`)}
                className="text-sm text-theme-primary hover:underline"
              >
                View all →
              </button>
            )}
          </div>
          <TopVideosCard videos={videos} />
        </div>
      </div>

      {/* Engagement Overview + Engagement Matrix side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-theme-secondary rounded-xl p-6 border border-theme-secondary">
          <h3 className="text-lg font-semibold text-theme-text mb-6">Engagement Overview</h3>
          <EngagementMetrics videos={videos} />
        </div>

        <div className="bg-theme-secondary rounded-xl p-6 border border-theme-secondary">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-theme-text">Engagement Matrix</h3>
              <p className="text-sm text-theme-text opacity-50 mt-0.5">
                Video distribution by likes and comments
              </p>
            </div>
            {videos.length > 0 && (
              <button
                onClick={() => navigate(`/audience/${audience.hash}/videos/`)}
                className="text-sm text-theme-primary hover:underline flex-shrink-0"
              >
                View all →
              </button>
            )}
          </div>
          {videos.length > 0 ? (
            <>
              <VideoPerformanceGrid videos={videos} compact />
              <div className="flex items-center justify-center gap-4 mt-3 text-[10px] text-theme-text opacity-40">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded" style={{ backgroundColor: 'rgba(59, 130, 246, 0.15)' }} />
                  <span>Few</span>
                  <div className="w-3 h-3 rounded" style={{ backgroundColor: 'rgba(59, 130, 246, 0.85)' }} />
                  <span>Many</span>
                </div>
              </div>
            </>
          ) : (
            <p className="text-sm text-theme-text opacity-50 py-4">No videos yet.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Analytics;
